/**
 * Offline outbox for worker updates. A log (note + photos + outcome) recorded without signal is kept in
 * IndexedDB on the device and replayed when the connection returns. Each entry carries a clientRef so the
 * server records it once even if it is sent twice; photos are uploaded first and their ids remembered,
 * so a retry never uploads the same photo again.
 */
import { useSyncExternalStore } from "react";
import { api, ApiError } from "./api";

export interface QueuedPhoto { name: string; type: string; blob: Blob }
export interface QueuedLog {
  clientRef: string;
  itemId: string;
  itemTitle: string;
  outcome: "continuing" | "done" | "blocked";
  note: string;
  photos: QueuedPhoto[];
  uploadedIds: string[];
  recordedAt: string;
  attempts: number;
  status: "pending" | "failed";
  error?: string;
}

/**
 * FEAT-02: any other small change made without signal (a task typed in, a checklist tick, a status or field
 * change) is kept as a plain request and replayed in order after the logs. Each carries a clientRef; creates
 * send it so the server makes the task once, and ticks / status / field changes are idempotent by nature.
 */
export interface QueuedRequest {
  clientRef: string;
  method: "POST" | "PATCH" | "PUT";
  path: string;
  body: unknown;
  /** what the banner says, e.g. "Add “Call the printer”" */
  label: string;
  itemId?: string;
  recordedAt: string;
  attempts: number;
  status: "pending" | "failed";
  error?: string;
}

const DB = "one-offline";
const STORE = "logs";
const REQS = "requests";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 2);
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE, { keyPath: "clientRef" });
      if (!r.result.objectStoreNames.contains(REQS)) r.result.createObjectStore(REQS, { keyPath: "clientRef" });
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

async function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>, store = STORE): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    t.oncomplete = () => resolve(req.result);
    t.onerror = () => reject(t.error);
  });
}

// ── tiny store so React can show the queue ───────────────────────────────────
let snapshot: { items: QueuedLog[]; requests: QueuedRequest[]; syncing: boolean; online: boolean } = { items: [], requests: [], syncing: false, online: typeof navigator === "undefined" ? true : navigator.onLine };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
async function refresh() {
  try {
    const items = await run<QueuedLog[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedLog[]>);
    const requests = await run<QueuedRequest[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedRequest[]>, REQS);
    snapshot = { ...snapshot, items: items.sort((a, b) => a.recordedAt.localeCompare(b.recordedAt)), requests: requests.sort((a, b) => a.recordedAt.localeCompare(b.recordedAt)) };
  } catch {
    snapshot = { ...snapshot, items: [], requests: [] };
  }
  emit();
}
export function useOutbox() {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => snapshot,
  );
}

export const newClientRef = () => crypto.randomUUID();

export async function enqueueLog(entry: Omit<QueuedLog, "attempts" | "status">) {
  await run("readwrite", (s) => s.put({ ...entry, attempts: 0, status: "pending" } satisfies QueuedLog));
  await refresh();
}

export async function discard(clientRef: string) {
  await run("readwrite", (s) => s.delete(clientRef));
  await run("readwrite", (s) => s.delete(clientRef), REQS);
  await refresh();
}

/**
 * Send a change now, or keep it on the device when there's no connection. Resolves {queued: true} in the
 * second case so the screen can show the change locally and skip the refetch. Server refusals still throw.
 */
export async function sendOrQueue<T = unknown>(r: Omit<QueuedRequest, "attempts" | "status" | "recordedAt" | "clientRef"> & { clientRef?: string; /** what to replay later, when it differs (e.g. without a version check) */ queueBody?: unknown }): Promise<{ queued: true } | { queued: false; result: T }> {
  const { clientRef = newClientRef(), queueBody, ...rest } = r;
  const keep = async () => {
    await run("readwrite", (s) => s.put({ ...rest, body: queueBody !== undefined ? queueBody : rest.body, clientRef, recordedAt: new Date().toISOString(), attempts: 0, status: "pending" } satisfies QueuedRequest), REQS);
    await refresh();
    return { queued: true as const };
  };
  if (typeof navigator !== "undefined" && !navigator.onLine && typeof indexedDB !== "undefined") return keep();
  try {
    const result = await api<T>(rest.path, { method: rest.method, json: rest.body });
    return { queued: false, result };
  } catch (err) {
    if (!isNetworkError(err) || typeof indexedDB === "undefined") throw err;
    return keep();
  }
}

/** True for "no connection" style failures, as opposed to the server refusing the request. */
export const isNetworkError = (e: unknown) => e instanceof TypeError || (e instanceof ApiError && [0, 502, 503, 504].includes(e.status));

async function uploadPhoto(p: QueuedPhoto): Promise<string> {
  const fd = new FormData();
  fd.append("file", new File([p.blob], p.name, { type: p.type }));
  const res = await fetch("/api/documents", { method: "POST", body: fd, credentials: "include" });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error ?? "upload_failed", body.message ?? "Photo upload failed", body.details);
  return body.id as string;
}

/** Send one update now: photos first, then the log. Throws ApiError (refused) or TypeError (offline). */
/** `fromQueue`: the update was saved offline and is being replayed, so the server keeps the worker's own time. */
export async function sendLog(e: Pick<QueuedLog, "clientRef" | "itemId" | "outcome" | "note" | "photos" | "uploadedIds" | "recordedAt">, onUploaded?: (ids: string[], remaining: QueuedPhoto[]) => Promise<void>, fromQueue = false) {
  const ids = [...e.uploadedIds];
  const remaining = [...e.photos];
  while (remaining.length) {
    ids.push(await uploadPhoto(remaining[0]!));
    remaining.shift();
    await onUploaded?.(ids, remaining);
  }
  const res = await fetch(`/api/work/items/${e.itemId}/logs`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ outcome: e.outcome, note: e.note || undefined, attachments: ids, clientRef: e.clientRef, recordedAt: fromQueue ? e.recordedAt : undefined }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error ?? "error", body.message ?? "Could not save the update", body.details);
  return body;
}

let flushing: Promise<number> | null = null;

/** Replay queued updates in order. Returns how many were sent. Stops at the first connection failure. */
export function flushOutbox(): Promise<number> {
  if (flushing) return flushing;
  flushing = (async () => {
    let sent = 0;
    const items = (await run<QueuedLog[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedLog[]>).catch(() => [] as QueuedLog[]))
      .filter((x) => x.status === "pending")
      .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
    const anyReq = (await run<number>("readonly", (s) => s.count(), REQS).catch(() => 0)) > 0;
    if (!items.length && !anyReq) return 0;
    snapshot = { ...snapshot, syncing: true };
    emit();
    try {
      for (const it of items) {
        try {
          await sendLog(it, async (uploadedIds, photos) => {
            await run("readwrite", (s) => s.put({ ...it, uploadedIds, photos }));
          }, true);
          await run("readwrite", (s) => s.delete(it.clientRef));
          sent++;
        } catch (err) {
          if (isNetworkError(err) || (err instanceof ApiError && [401, 408, 429].includes(err.status))) break; // try again later
          const latest = await run<QueuedLog | undefined>("readonly", (s) => s.get(it.clientRef) as IDBRequest<QueuedLog | undefined>);
          await run("readwrite", (s) => s.put({ ...(latest ?? it), attempts: it.attempts + 1, status: "failed", error: (err as Error).message }));
        }
      }
      // then the other changes, oldest first; a refusal marks that one failed and the rest carry on
      const reqs = (await run<QueuedRequest[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedRequest[]>, REQS).catch(() => [] as QueuedRequest[]))
        .filter((x) => x.status === "pending")
        .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
      for (const r of reqs) {
        try {
          await api(r.path, { method: r.method, json: r.body });
          await run("readwrite", (s) => s.delete(r.clientRef), REQS);
          sent++;
        } catch (err) {
          if (isNetworkError(err) || (err instanceof ApiError && [401, 408, 429].includes(err.status))) break;
          await run("readwrite", (s) => s.put({ ...r, attempts: r.attempts + 1, status: "failed", error: (err as Error).message }), REQS);
        }
      }
    } finally {
      snapshot = { ...snapshot, syncing: false };
      await refresh();
    }
    return sent;
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}

export async function retry(clientRef: string) {
  const it = await run<QueuedLog | undefined>("readonly", (s) => s.get(clientRef) as IDBRequest<QueuedLog | undefined>);
  if (it) await run("readwrite", (s) => s.put({ ...it, status: "pending", error: undefined }));
  const rq = await run<QueuedRequest | undefined>("readonly", (s) => s.get(clientRef) as IDBRequest<QueuedRequest | undefined>, REQS);
  if (rq) await run("readwrite", (s) => s.put({ ...rq, status: "pending", error: undefined }), REQS);
  await refresh();
  return flushOutbox();
}

/** Call once at start-up: replays on reconnect, every 30 s while items wait, and when the app comes back to the foreground. */
export function startOutbox(onSent: (n: number) => void) {
  if (typeof indexedDB === "undefined") return;
  const tick = () => void flushOutbox().then((n) => n && onSent(n));
  const setOnline = () => {
    snapshot = { ...snapshot, online: navigator.onLine };
    emit();
    if (navigator.onLine) tick();
  };
  window.addEventListener("online", setOnline);
  window.addEventListener("offline", setOnline);
  document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && tick());
  setInterval(() => (snapshot.items.some((i) => i.status === "pending") || snapshot.requests.some((i) => i.status === "pending")) && navigator.onLine && tick(), 30_000);
  void refresh().then(tick);
}

/** Forget everything queued on this device (sign-out on a shared phone). */
export async function clearOutbox() {
  try {
    await run("readwrite", (s) => s.clear());
    await run("readwrite", (s) => s.clear(), REQS);
  } catch {
    /* nothing stored */
  }
  await refresh();
}
